import { useCallback, useEffect, useState } from 'react';
import { useClient } from 'shared/apollo';
import { saveSurveyAnswer } from './api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { CustomQuestionAnswer } from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import { useUser } from 'shared/auth/context';
import { ReportCustomQuestionTypePropsBase } from './utils';
import { logError } from 'shared/utils/logError';

interface SaveSurveyAnswerProps {
  initialAnswer: string;
  initialEdit?: boolean;
  onChange: ReportCustomQuestionTypePropsBase['onChange'];
  reportId: ReportCustomQuestionTypePropsBase['reportId'];
  question: ReportCustomQuestionTypePropsBase['question'];
  customQuestionIndex: ReportCustomQuestionTypePropsBase['customQuestionIndex'];
  customQuestion: ReportCustomQuestionTypePropsBase['customQuestion'];
  questionIndex: ReportCustomQuestionTypePropsBase['questionIndex'];
  handleAddDoneQuestionId: ReportCustomQuestionTypePropsBase['handleAddDoneQuestionId'];
  handleRemoveDoneQuestionId: ReportCustomQuestionTypePropsBase['handleRemoveDoneQuestionId'];
}

export const useSaveSurveyAnswer = ({
  reportId,
  question,
  customQuestionIndex,
  customQuestion,
  initialAnswer,
  initialEdit = true,
  questionIndex,
  onChange,
  handleAddDoneQuestionId,
  handleRemoveDoneQuestionId,
}: SaveSurveyAnswerProps) => {
  const user = useUser();
  const client = useClient();
  const [isEdit, setEdit] = useState(initialEdit);
  const [requestState, makeRequest] = useGraphQLRequest<{
    reportSurveyAnswer: {
      id: number;
      success: boolean;
    };
  }>();

  const handleShowEdit = useCallback(() => {
    setEdit(true);
    handleRemoveDoneQuestionId(question.dbId);
  }, [setEdit, handleRemoveDoneQuestionId]);
  const handleHideEdit = useCallback(() => {
    setEdit(false);
  }, [setEdit]);
  const handleCancelEdit = useCallback(() => {
    setEdit(false);
    handleAddDoneQuestionId(question.dbId);
  }, [setEdit, handleAddDoneQuestionId, question.dbId]);

  const handleSaveSurveyAnswer = ({
    answer,
    answerId,
  }: {
    answer: CustomQuestionAnswer;
    answerId: number | string | undefined;
  }) => {
    if (!client) return;
    if (answer.value === initialAnswer) {
      setEdit(false);
      handleAddDoneQuestionId(question.dbId);
      return;
    }
    const request = saveSurveyAnswer(client, {
      id: answerId,
      value: answer.value,
      questionId: question.dbId,
      reportId: reportId,
      optionId: answer.optionId,
      npoId: user.attributes['custom:account_id'],
      originalQuestionText: question.questionText,
    });
    makeRequest(request);
    request
      .then(r => {
        if (r.data.reportSurveyAnswer.success) {
          setEdit(false);
          onChange(customQuestionIndex, questionIndex, question.dbId, {
            dbId: r.data.reportSurveyAnswer.id,
            optionId: answer.optionId,
            value: answer.value,
          });
        } else {
          handleRemoveDoneQuestionId(question.dbId);
        }
      })
      .catch(e => {
        logError('ReportCustomQuestionType', e);
        handleRemoveDoneQuestionId(question.dbId);
      });
  };

  return {
    requestState,
    handleSaveSurveyAnswer,
    isEdit,
    handleShowEdit,
    handleHideEdit,
    handleCancelEdit,
  };
};
